import Widgets from 'js-widget-hooks';

// Basic style is the same as we use in https://dw-connect.org/centers (see dwconnect-2/map.component.ts)
const BasicStyle = 'mapbox://styles/dw-connect/ckmduc3rbakht17q3iv0a98zb';
const StreetsStyle = 'mapbox://styles/mapbox/streets-v11';
const SatelliteStyle = 'mapbox://styles/mapbox/satellite-streets-v12';

const PhotosCtaHtml =
  'Help us illustrate this map! If you have photos of these places and hold the copyright, please send them to <a href="mailto:mlp@dw-connect.org">mlp@dw-connect.org</a>';

const ZoomFactorThumbnail = 1;
const ZoomFactorNormal = 9;
const DefaultCategory = '';

// Trying to parse GeoJSON, see the example at the bottom
export function parseStringToGeoJSON(pathString) {
  try {
    const obj = JSON.parse(pathString);

    return (obj !== null && obj.type !== undefined) ? obj : null;
  } catch (e) {
    if (!(e instanceof SyntaxError)) {
      throw e; // re-throw the unknown error
    }
  }
}

// Get the map center from GeoJSON "map-center": true Point, or use a default
export function getMapCenter(mapJson, defaultLng, defaultLat) {
  var lng = defaultLng;
  var lat = defaultLat;

  // Check if the input is a valid GeoJSON FeatureCollection
  if (mapJson && mapJson.type === 'FeatureCollection') {
    // Loop through each feature in the collection
    mapJson.features.forEach((feature) => {
      // Check if the feature is a point and has the desired property
      if (
        feature.geometry.type === 'Point' &&
        feature.properties &&
        feature.properties['map-center'] === true
      ) {
        lng = feature.geometry.coordinates[0] ?? lng;
        lat = feature.geometry.coordinates[1] ?? lat;
      }
    });
  }

  return [lng, lat];
}

// Returns the  objects to create standard layers from them (fill, outline, line).
// See https://geojson.io/ sources, map/index.js
export function getMapboxLayers(category, source, defaultStrokeColor) {
  return [
    {
      id: `${category}-fill`,
      type: 'fill',
      source: source,
      paint: {
        'fill-color': ['coalesce', ['get', 'fill'], defaultStrokeColor],
        'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.2],
      },
      filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', [
        'coalesce',
        ['get', 'category'],
        DefaultCategory,
      ], category]],
    },
    {
      id: `${category}-outline`,
      type: 'line',
      source: source,
      paint: {
        'line-color': ['coalesce', ['get', 'stroke'], defaultStrokeColor],
        'line-width': ['coalesce', ['get', 'stroke-width'], 2],
        'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 1],
      },
      filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', [
        'coalesce',
        ['get', 'category'],
        DefaultCategory,
      ], category]],
    },
    {
      id: `${category}-line`,
      type: 'line',
      source: source,
      paint: {
        'line-color': ['coalesce', ['get', 'stroke'], defaultStrokeColor],
        'line-width': ['coalesce', ['get', 'stroke-width'], 2],
        'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 1],
      },
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', [
        'coalesce',
        ['get', 'category'],
        DefaultCategory,
      ], category]],
    },
  ];
}

// Returns the markers with popups ready to be added to map.
// See https://geojson.io/ sources, map/util.js
function getMapboxMarkers(feature, defaultMarkerColor) {
  const pointFeatures = [];
  const { geometry, properties } = feature;

  // wrap point geometry in a feature and push, if there is a marker
  const addPointFeature = (geometry, properties) => {
    if (properties && properties['marker-symbol']) {
      pointFeatures.push({
        type: 'Feature',
        geometry,
        properties,
      });
    }
  };

  const handleGeometry = (geometry, properties) => {
    if (geometry.type === 'Point') {
      addPointFeature(geometry, properties);
    }

    // Recursion FTW
    if (geometry.type === 'MultiPoint') {
      geometry.coordinates.forEach((coordinatePair) => {
        addPointFeature(
          {
            type: 'Point',
            coordinates: coordinatePair,
          },
          properties || {},
        );
      });
    }

    // More recursions!
    if (geometry.type === 'GeometryCollection') {
      geometry.geometries.forEach((geometry) => {
        handleGeometry(geometry, properties);
      });
    }
  };

  handleGeometry(geometry, properties);

  return pointFeatures.map((d, i) => {
    const color = (d.properties && d.properties['marker-color']) ||
      defaultMarkerColor;

    let scale = 1;
    if (d.properties && d.properties['marker-size']) {
      if (d.properties['marker-size'] === 'small') {
        scale = 0.6;
      }

      if (d.properties['marker-size'] === 'large') {
        scale = 1.2;
      }
    }

    let popup = null;

    if (d.properties.description) {
      const descriptionWithCta = d.properties.description + '<br><br>' +
        PhotosCtaHtml;
      popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
        descriptionWithCta,
      );
    }

    return new mapboxgl.Marker({
      color: color,
      scale: scale,
    })
      .setPopup(popup)
      .setLngLat(d.geometry.coordinates);
  });
}

// Class to manage layers & markers grouped by categories
class CategoryObjects {
  constructor(map) {
    this.map = map;

    this.markers = [];
    this.layers = [];

    this.visible = true;
    this.defaultColor = null;
  }

  addLayer(layer) {
    this.layers.push(layer);
  }

  addLayers(layers) {
    layers.forEach((layer) => {
      this.addLayer(layer);
    });
  }

  addMarker(marker) {
    this.markers.push(marker);
  }

  addMarkers(markers) {
    markers.forEach((marker) => {
      this.addMarker(marker);
    });
  }

  show() {
    this.visible = true;

    this._showLayers();
    this._showMarkers();
  }

  hide() {
    this.visible = false;

    this._hideLayers();
    this._hideMarkers();
  }

  applyToMap() {
    this._applyLayers();
    this._applyMarkers();
  }

  setDefaultColor(defaultColor) {
    if (!defaultColor) {
      return;
    }

    if (!this.defaultColor) {
      this.defaultColor = defaultColor;
    }
  }

  _showLayers() {
    this.layers.forEach((layer) => {
      this.map.setLayoutProperty(layer.id, 'visibility', 'visible');
    });
  }

  _hideLayers() {
    this.layers.forEach((layer) => {
      this.map.setLayoutProperty(layer.id, 'visibility', 'none');
    });
  }

  _showMarkers() {
    this.markers.forEach((marker) => {
      marker.addTo(this.map);
    });
  }

  _hideMarkers() {
    this.markers.forEach((marker) => {
      marker.remove();
    });
  }

  _applyLayers() {
    this.layers.forEach((layer) => {
      if (!layer.layout) {
        layer.layout = {};
      }

      layer.layout.visibility = (this.visible) ? 'visible' : 'none';

      if (!this.map.getLayer(layer.id)) {
        this.map.addLayer(layer); // Restore the layer after style change, etc
      }
    });
  }

  _applyMarkers() {
    this.markers.forEach((marker) => {
      if (this.visible) {
        if (marker._map) {
          return; // Already there
        } else {
          marker.addTo(this.map); // Missing, let's add
        }
      } else {
        if (!marker.map) {
          return; // Not on the map, we're good
        } else {
          marker.remove(); // Present, let's remove
        }
      }
    });
  }
}

// The categories control, responsible for showing/hiding category buttons
// See https://codepen.io/roblabs/pen/zJjPzX
class CategoriesControl {
  constructor(
    categories,
  ) {
    this._categories = categories;
  }

  _addButton(category) {
    let button = document.createElement('button');

    button.type = 'button';
    button.title = category;

    let objects = this._categories[category];

    let marker = new mapboxgl.Marker({
      color: objects.defaultColor,
      scale: 0.4,
    });

    button.appendChild(marker.getElement());

    button.onclick = () => {
      if (objects.visible) {
        objects.hide();
      } else {
        objects.show();
      }
    };

    return button;
  }

  onAdd(map) {
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

    for (const category in this._categories) {
      this._container.appendChild(this._addButton(category));
    }

    return this._container;
  }

  onRemove() {
    if (this._container) {
      this._container.parentNode.removeChild(this._container);
      this._container = undefined;
    }
  }
}

// The control to switch between styles (e.g. streets, satellite, etc)
class StyleButtonControl {
  constructor(
    className,
    styles,
  ) {
    this._className = className;
    this._styles = styles;
    this._styleNumber = -1;
  }

  onAdd(map) {
    this._button = document.createElement('button');
    this._button.className = 'mapboxgl-ctrl-icon ' + this._className;
    this._button.type = 'button';
    this._map = map;

    // Identify the current style from the map style
    const currentMapStyle = map.getStyle().name;
    this._styles.forEach((style, index) => {
      if (currentMapStyle === style.styleName) {
        this._styleNumber = index;
      }
    });

    this._updateTitle();

    this._button.onclick = () => {
      if (this._map && this._styles) {
        this._styleNumber = this.getNextStyleNumber(); // If the style was not found, it will be just 0
        this._map.setStyle(this.getStyle().styleUrl);
        this._updateTitle();
      }
    };

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this._container.appendChild(this._button);

    return this._container;
  }

  onRemove() {
    if (this._container) {
      this._container.parentNode.removeChild(this._container);
      this._container = undefined;
      this._button = undefined;
      this._map = undefined;
    }
  }

  getNextStyleNumber() {
    if (this._styleNumber + 1 >= this._styles.length) {
      return 0;
    } else {
      return this._styleNumber + 1;
    }
  }

  getStyle(next = false) {
    const number = next ? this.getNextStyleNumber() : this._styleNumber;

    return this._styles[number];
  }

  _updateTitle() {
    if (!this._styles) {
      return;
    }

    const nextStyle = this.getStyle(true);
    this._button.innerHTML = nextStyle.label;
  }
}

// Adds an info button that shows a photo-submission popup on click
class PhotosInfoControl {
  constructor(className) {
    this._className = className;
  }

  onAdd(map) {
    this._button = document.createElement('button');
    this._button.className = 'mapboxgl-ctrl-icon ' + this._className;
    this._button.type = 'button';
    this._button.title = 'Submit photos';
    this._button.innerText = '📷';

    this._map = map;

    this._popup = new mapboxgl.Popup({
      closeOnClick: false,
      maxWidth: '300px',
      className: 'map-photos-popup',
    })
      .setHTML(PhotosCtaHtml);

    this._button.onclick = () => {
      if (this._popup.isOpen()) {
        this._popup.remove();
      } else {
        this._popup.setLngLat(this._map.getCenter()).addTo(this._map);
      }
    };

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this._container.appendChild(this._button);

    return this._container;
  }

  onRemove() {
    this._popup.remove();
    if (this._container) {
      this._container.parentNode.removeChild(this._container);
      this._container = undefined;
      this._button = undefined;
    }
    this._map = undefined;
  }
}

// Adds the Home button to reset the map center and zoom
class HomeControl {
  constructor(
    center,
    className,
  ) {
    this._center = center;
    this._className = className;
  }

  onAdd(map) {
    this._button = document.createElement('button');
    this._button.className = 'mapboxgl-ctrl-icon ' + this._className;
    this._button.type = 'button';
    this._button.title = 'Home';
    this._button.innerText = '🏠';

    this._map = map;

    this._button.onclick = () => {
      if (this._map && this._center) {
        this._map.setCenter(this._center);
        this._map.setZoom(ZoomFactorNormal);
      }
    };

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this._container.appendChild(this._button);

    return this._container;
  }

  onRemove() {
    if (this._container) {
      this._container.parentNode.removeChild(this._container);
      this._container = undefined;
      this._button = undefined;
      this._map = undefined;
    }
  }
}

Widgets.register('map', async function (elem) {
  // @krvss/mapboxgl-minimap, and this file's own code below, expect
  // window.mapboxgl as a plain global - mapbox-gl doesn't self-register it
  // under Bun's bundler the way it did under webpack.
  window.mapboxgl = (await import('mapbox-gl')).default;
  await import('@krvss/mapboxgl-minimap');

  // Setting the default map center (Lumbini)
  const [defaultLng, defaultLat] = [84.9913, 24.696];

  // Loading the GeoJSON and getting the map center
  const mapJSON = parseStringToGeoJSON(elem.dataset.path_coordinates);

  if (mapJSON == null) {
    console.error(`No GeoJSON found: ${elem.dataset.path_coordinates}`);
    return;
  } else {
    console.log(`Path GeoJSON: ${JSON.stringify(mapJSON)}`);
  }

  const [centerLng, centerLat] = getMapCenter(mapJSON, defaultLng, defaultLat);

  const thumbnailMode = elem.dataset.thumbnail === '1';
  // Detecting mobile device
  const isMobileDevice = /Mobi/i.test(window.navigator.userAgent);

  console.log(
    `Map center: long - ${centerLng}, lat - ${centerLat}; thumbnail mode: ${thumbnailMode}; mobile device: ${isMobileDevice}`,
  );

  // Mapbox stuff started
  mapboxgl.accessToken =
    'pk.eyJ1IjoiZHctY29ubmVjdCIsImEiOiJja21kdThjcGQycGZrMnFqeDhzbnhvOXg1In0.kdHv4ZAzwh1SfGC5D54pTw';

  // The actual map
  const teacherMap = new mapboxgl.Map({
    container: elem,
    style: BasicStyle,
    center: [centerLng, centerLat], // starting position [lng, lat]
    zoom: thumbnailMode ? ZoomFactorThumbnail : ZoomFactorNormal,
    projection: 'mercator',
  });

  // Initial zoom level depending on the thumbnail mode
  let zoomedFullscreen = false;

  // Init the controls
  const fullscreenControl = new mapboxgl.FullscreenControl();
  const fullScreenButtons = [
    new mapboxgl.NavigationControl({ showCompass: false }),
    new StyleButtonControl(
      'class-style-todo',
      [
        {
          label: '📍',
          styleName: 'Basic',
          styleUrl: BasicStyle,
        },
        {
          label: '🌍',
          styleName: 'Mapbox Satellite',
          styleUrl: SatelliteStyle,
        },
      ],
    ),
    new HomeControl({
      lng: centerLng,
      lat: centerLat,
    }, 'class-style-todo'),
    new PhotosInfoControl('class-style-todo'),
  ];

  const minimapControl = isMobileDevice ? null : new mapboxgl.Minimap({
    center: [centerLng, centerLat],
    zoom: 4,
    lineColor: 'white',
    lineWidth: 2,
    style: SatelliteStyle,
  });

  // Full screen is always there
  teacherMap.addControl(fullscreenControl);

  const mapSource = {
    'type': 'geojson',
    'data': {
      'type': 'FeatureCollection',
      'features': mapJSON.features,
    },
  };

  // Dict of category name to category object
  const mapCategories = {};

  // Group objects by categories
  mapJSON.features.forEach((feature) => {
    const category = feature.properties.category || DefaultCategory;
    let objects = mapCategories[category];

    if (!objects) {
      objects = new CategoryObjects(teacherMap);
      mapCategories[category] = objects;
    }

    // Collect all markers across all features into a category
    objects.addMarkers(getMapboxMarkers(feature, 'blue'));

    // Set the default color, if not yet
    objects.setDefaultColor(
      feature.properties['line-color'] ??
        feature.properties['fill-color'] ?? feature.properties['marker-color'],
    );
  });

  // Add categories on/off control, if any
  if (mapCategories) {
    const categoriesControl = new CategoriesControl(mapCategories);
    fullScreenButtons.push(categoriesControl);
  }

  const defaultPathColor = elem.dataset.path_color || 'red';

  // Now add layers per category
  for (const category in mapCategories) {
    const objects = mapCategories[category];
    objects.addLayers(
      getMapboxLayers(category, 'travelSource', defaultPathColor),
    );
    objects.setDefaultColor(defaultPathColor);
  }

  // Process the route once the map is alive
  teacherMap.on('load', () => {
    const applyTravel = () => {
      teacherMap.addSource('travelSource', mapSource);

      for (const category in mapCategories) {
        const objects = mapCategories[category];
        objects.applyToMap();
      }
    };

    applyTravel();

    const showFullScreenControls = () => {
      fullScreenButtons.forEach((control) => {
        teacherMap.addControl(control);
      });

      if (minimapControl) {
        teacherMap.addControl(minimapControl, 'bottom-right');
      }
    };

    const hideFullScreenControls = () => {
      fullScreenButtons.forEach((control) => {
        teacherMap.removeControl(control);
      });

      if (minimapControl) {
        teacherMap.removeControl(minimapControl);
      }
    };

    if (thumbnailMode) {
      // We want to show some controls in the fullscreen mode if we're starting from thumbnail
      // We use some internal machinery of fullscreenControl because it is Mapbox product
      window.document.addEventListener(
        fullscreenControl._fullscreenchange,
        () => {
          if (fullscreenControl._fullscreen) {
            showFullScreenControls();

            if (!zoomedFullscreen) {
              teacherMap.setZoom(ZoomFactorNormal);

              teacherMap.setCenter({
                lng: centerLng,
                lat: centerLat,
              });

              zoomedFullscreen = true;
            }
          } else {
            hideFullScreenControls();
          }
        },
      );
    } else {
      showFullScreenControls();
    }

    // Connect to style change to restore the source and layers
    teacherMap.on('style.load', applyTravel);

    console.log('Map loaded');
  });
});
